import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Grid3X3,
  Star,
  ThumbsUp,
  Lightbulb,
  Copy,
  Check,
  Flag,
  UserPlus,
  UserMinus,
  X,
  Users,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { PageLayout } from '../../components/styled/PageLayout';
import { Container } from '../../components/styled/Container';
import SEO from '../../components/seo/SEO';
import { regionFromPathname } from '../../utils/regionFromPath';
import { pathWithRegion } from '../../utils/navBase';
import { detailPath, similarPath } from '../../utils/paths';
import * as userProfileService from '../../services/userProfileService';

const Shell = styled.div`
  padding: 1.5rem 0 3rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 2.5rem;
  }
`;

const Avatar = styled.div`
  width: 5.5rem;
  height: 5.5rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.muted};
  border: 3px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  flex-shrink: 0;
  @media (min-width: 768px) {
    width: 8rem;
    height: 8rem;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeadMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const Username = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.foreground};
`;

const DisplayName = styled.p`
  margin: 0 0 0.35rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
`;

const Bio = styled.p`
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  line-height: 1.5;
  max-width: 36rem;
  white-space: pre-wrap;
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  margin: 0.75rem 0;
`;

const StatBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: ${({ theme }) => theme.colors.foreground};
  &:hover {
    opacity: 0.85;
  }
`;

const StatNum = styled.span`
  font-weight: 800;
  margin-right: 0.25rem;
`;

const StatLabel = styled.span`
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: none;
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  cursor: pointer;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  &:hover {
    opacity: 0.92;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnGhost = styled(Btn)`
  background: ${({ theme }) => theme.colors.muted};
  color: ${({ theme }) => theme.colors.foreground};
`;

const BtnDanger = styled(BtnGhost)`
  color: ${({ theme }) => theme.colors.crimson};
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.25rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: 1.5rem;
  padding-top: 1rem;
`;

const Tab = styled.button`
  flex: 1;
  max-width: 8rem;
  padding: 0.65rem;
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  cursor: pointer;
  background: ${({ $active, theme }) => ($active ? theme.colors.muted : 'transparent')};
  color: ${({ theme }) => theme.colors.foreground};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  margin-top: 1rem;
  @media (min-width: 600px) {
    gap: 4px;
  }
`;

const Cell = styled(Link)`
  aspect-ratio: 2/3;
  background: ${({ theme }) => theme.colors.muted};
  position: relative;
  overflow: hidden;
  display: block;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &:hover {
    opacity: 0.92;
  }
`;

const CellOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.75), transparent 55%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0.35rem;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.2;
  @media (min-width: 600px) {
    font-size: ${({ theme }) => theme.fontClamp.xs};
    padding: 0.5rem;
  }
`;

const Empty = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.mutedForeground};
  padding: 2rem;
`;

const ErrorBox = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ErrorHomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.25rem;
  padding: 0.55rem 1.15rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  text-decoration: none;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  &:hover {
    opacity: 0.92;
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  padding: 1.25rem;
  max-width: 22rem;
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadowCardHover};
`;

const ListModal = styled(ModalCard)`
  max-width: 24rem;
  max-height: 70vh;
  overflow: auto;
`;

const ListRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.5rem 0;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.foreground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
  }
`;

const MiniAv = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.muted};
  overflow: hidden;
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

function useProfileRegion() {
  const { region: seg } = useParams();
  const { pathname } = useLocation();
  if (seg && /^[a-z]{2}$/i.test(seg) && pathname.includes('/profile/')) return seg.toLowerCase();
  return regionFromPathname(pathname);
}

function posterFallback() {
  return 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="200" height="300" fill="%23cbd5e1"><rect width="100%" height="100%"/></svg>');
}

export default function UserProfilePage() {
  const { username: usernameParam } = useParams();
  const region = useProfileRegion();
  const navigate = useNavigate();
  const auth = useSelector((s) => s.auth);

  const username = String(usernameParam || '').toLowerCase();

  const [profile, setProfile] = useState(null);
  const [counts, setCounts] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isOwn, setIsOwn] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actLoading, setActLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [followBusy, setFollowBusy] = useState(false);
  const [listOpen, setListOpen] = useState(null);
  const [listItems, setListItems] = useState([]);
  const lastTab = useRef(tab);

  const profileUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${pathWithRegion(`/profile/${username}`, region)}`;
  }, [username, region]);

  const loadProfile = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setLoadErr('');
    try {
      const data = await userProfileService.getPublicProfile(username);
      setProfile(data.user);
      setCounts(data.counts);
      setIsFollowing(Boolean(data.isFollowing));
      setIsOwn(Boolean(data.isOwnProfile));
    } catch (e) {
      setLoadErr(e?.response?.data?.error || e?.message || 'Profile not found');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    setTab('all');
    setPage(1);
    setItems([]);
    lastTab.current = 'all';
  }, [username]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!profile) return;
    if (lastTab.current !== tab) {
      lastTab.current = tab;
      if (page !== 1) {
        setPage(1);
        return;
      }
      setItems([]);
    }
    let cancelled = false;
    setActLoading(true);
    const type = tab === 'all' ? 'all' : tab;
    userProfileService
      .getPublicActivity(username, { type, page, limit: 24 })
      .then((data) => {
        if (cancelled) return;
        const next = data.items || [];
        setItems((prev) => (page === 1 ? next : [...prev, ...next]));
        setHasMore(Boolean(data.hasMore));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setActLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile, username, tab, page]);

  const openList = async (kind) => {
    setListOpen(kind);
    try {
      const fn = kind === 'followers' ? userProfileService.getFollowers : userProfileService.getFollowing;
      const data = await fn(username, 1);
      setListItems(data.items || []);
    } catch {
      setListItems([]);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(profileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const toggleFollow = async () => {
    if (!auth.isAuthenticated) {
      navigate(pathWithRegion('/login', region));
      return;
    }
    setFollowBusy(true);
    try {
      if (isFollowing) {
        await userProfileService.unfollowUser(username);
        setIsFollowing(false);
      } else {
        await userProfileService.followUser(username);
        setIsFollowing(true);
      }
      loadProfile();
    } catch {
      /* ignore */
    } finally {
      setFollowBusy(false);
    }
  };

  const submitReport = async () => {
    try {
      await userProfileService.reportUser(username, reportReason);
      setReportOpen(false);
      setReportReason('');
    } catch {
      /* ignore */
    }
  };

  const cellHref = (item) => {
    const rp = region || '';
    if (item.type === 'review') {
      return detailPath(item.baseCategory, item.baseId, item.title, rp);
    }
    if (item.type === 'vote') {
      return similarPath(item.baseCategory, item.baseId, item.baseTitle, rp);
    }
    if (item.type === 'suggestion') {
      return detailPath(item.baseCategory, item.baseId, item.baseTitle, rp);
    }
    return pathWithRegion('/', region);
  };

  const cellImage = (item) => {
    if (item.type === 'review') return item.posterUrl || posterFallback();
    if (item.type === 'vote') return item.basePosterUrl || posterFallback();
    if (item.type === 'suggestion') return item.basePosterUrl || posterFallback();
    return posterFallback();
  };

  const cellLabel = (item) => {
    if (item.type === 'vote') return `${item.rating}/5 · ${item.baseTitle}`;
    if (item.type === 'review') return `${item.rating}/5 · ${item.title}`;
    return item.baseTitle || item.suggestedTitle || '—';
  };

  if (loading) {
    return (
      <PageLayout>
        <Container>
          <Shell>
            <Empty>Loading…</Empty>
          </Shell>
        </Container>
      </PageLayout>
    );
  }

  if (loadErr || !profile) {
    const isPrivate = String(loadErr || '').toLowerCase().includes('private');
    const message = isPrivate
      ? 'This profile is set to private. Only the owner can view it when signed in.'
      : loadErr || 'Profile unavailable.';
    return (
      <PageLayout>
        <SEO region={region} path={`/profile/${username}`} title="Profile" noindex />
        <Container>
          <ErrorBox>
            <p style={{ margin: 0, maxWidth: '24rem', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>{message}</p>
            <ErrorHomeLink to={pathWithRegion('/', region)}>Home</ErrorHomeLink>
          </ErrorBox>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEO
        region={region}
        path={`/profile/${username}`}
        title={`@${profile.username} · FliqMatch`}
        description={profile.bio || `${profile.displayName || profile.username} on FliqMatch`}
      />
      <Container>
        <Shell>
          <Header>
            <Avatar>
              {profile.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : <User size={48} style={{ margin: 'auto', opacity: 0.35 }} />}
            </Avatar>
            <HeadMain>
              <RowTop>
                <Username>@{profile.username}</Username>
                {!isOwn && auth.isAuthenticated && (
                  <Btn type="button" onClick={toggleFollow} disabled={followBusy}>
                    {isFollowing ? (
                      <>
                        <UserMinus size={16} /> Following
                      </>
                    ) : (
                      <>
                        <UserPlus size={16} /> Follow
                      </>
                    )}
                  </Btn>
                )}
                <BtnGhost type="button" onClick={copyLink}>
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied' : 'Copy link'}
                </BtnGhost>
                {!isOwn && auth.isAuthenticated && (
                  <BtnDanger type="button" onClick={() => setReportOpen(true)}>
                    <Flag size={16} /> Report
                  </BtnDanger>
                )}
              </RowTop>
              {profile.displayName ? <DisplayName>{profile.displayName}</DisplayName> : null}
              {profile.bio ? <Bio>{profile.bio}</Bio> : null}
              <Stats>
                <StatBtn type="button" onClick={() => openList('followers')}>
                  <StatNum>{counts?.followers ?? 0}</StatNum>
                  <StatLabel>followers</StatLabel>
                </StatBtn>
                <StatBtn type="button" onClick={() => openList('following')}>
                  <StatNum>{counts?.following ?? 0}</StatNum>
                  <StatLabel>following</StatLabel>
                </StatBtn>
                <div>
                  <StatNum>{counts?.reviews ?? 0}</StatNum>
                  <StatLabel> reviews</StatLabel>
                </div>
                <div>
                  <StatNum>{counts?.votes ?? 0}</StatNum>
                  <StatLabel> votes</StatLabel>
                </div>
                <div>
                  <StatNum>{counts?.suggestions ?? 0}</StatNum>
                  <StatLabel> suggestions</StatLabel>
                </div>
              </Stats>
              <Actions>
                {isOwn && (
                  <Btn type="button" onClick={() => navigate(pathWithRegion('/dashboard', region))}>
                    Edit profile
                  </Btn>
                )}
              </Actions>
              {(profile.createdAt || profile.lastLoginAt) && (
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                  {profile.createdAt && (
                    <>
                      Member since {new Date(profile.createdAt).toLocaleDateString()}
                      {profile.lastLoginAt ? ' · ' : ''}
                    </>
                  )}
                  {profile.lastLoginAt && <>Last active {new Date(profile.lastLoginAt).toLocaleDateString()}</>}
                </p>
              )}
            </HeadMain>
          </Header>

          <Tabs>
            <Tab type="button" $active={tab === 'all'} onClick={() => setTab('all')}>
              <Grid3X3 size={16} /> All
            </Tab>
            <Tab type="button" $active={tab === 'reviews'} onClick={() => setTab('reviews')}>
              <Star size={16} /> Reviews
            </Tab>
            <Tab type="button" $active={tab === 'votes'} onClick={() => setTab('votes')}>
              <ThumbsUp size={16} /> Votes
            </Tab>
            <Tab type="button" $active={tab === 'suggestions'} onClick={() => setTab('suggestions')}>
              <Lightbulb size={16} /> Ideas
            </Tab>
          </Tabs>

          {actLoading && page === 1 ? (
            <Empty>Loading activity…</Empty>
          ) : items.length === 0 ? (
            <Empty>No posts yet.</Empty>
          ) : (
            <Grid>
              {items.map((item) => (
                <Cell key={item.id} to={cellHref(item)}>
                  <img src={cellImage(item)} alt="" loading="lazy" />
                  <CellOverlay>{cellLabel(item)}</CellOverlay>
                </Cell>
              ))}
            </Grid>
          )}

          {hasMore && !actLoading && (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <BtnGhost type="button" onClick={() => setPage((p) => p + 1)}>
                Load more
              </BtnGhost>
            </div>
          )}
        </Shell>
      </Container>

      <AnimatePresence>
        {reportOpen && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setReportOpen(false)}
          >
            <ModalCard onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong>Report profile</strong>
                <button type="button" style={{ border: 'none', background: 'none', cursor: 'pointer' }} onClick={() => setReportOpen(false)}>
                  <X size={20} />
                </button>
              </div>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Reason (optional)"
                rows={4}
                style={{ width: '100%', marginBottom: '0.75rem', padding: '0.5rem', borderRadius: 8, border: '1px solid #e2e8f0' }}
              />
              <Btn type="button" onClick={submitReport}>
                Submit
              </Btn>
            </ModalCard>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {listOpen && (
          <Modal initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setListOpen(null)}>
            <ListModal onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <strong>{listOpen === 'followers' ? 'Followers' : 'Following'}</strong>
                <button type="button" style={{ border: 'none', background: 'none', cursor: 'pointer' }} onClick={() => setListOpen(null)}>
                  <X size={20} />
                </button>
              </div>
              {listItems.length === 0 ? (
                <Empty style={{ padding: '1rem' }}>No users yet.</Empty>
              ) : (
                listItems.map((u) => (
                  <ListRow key={u.username} to={pathWithRegion(`/profile/${u.username}`, region)} onClick={() => setListOpen(null)}>
                    <MiniAv>{u.avatarUrl ? <img src={u.avatarUrl} alt="" /> : <Users size={18} style={{ margin: 'auto', opacity: 0.4 }} />}</MiniAv>
                    <div>
                      <div style={{ fontWeight: 700 }}>@{u.username}</div>
                      {u.displayName ? <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{u.displayName}</div> : null}
                    </div>
                  </ListRow>
                ))
              )}
            </ListModal>
          </Modal>
        )}
      </AnimatePresence>
    </PageLayout>
  );
}
